import {type ContainerStatusEntity, scadaMode, type TransportCategory} from "@ots-frontend/ots-data";

/** How the container status grid marks rows; per user, safe to persist. */
export type ContainerStatusDisplayOptions = {
  /** Highlights containers whose latest poll is older than `stalePollHours`. */
  markStalePolls: boolean;
  stalePollHours: number;
  /** Also flags containers with no poll at all; off because many sites are gauged by hand. */
  staleIncludeNeverPolled: boolean;
  staleSkipDoNotDispatch: boolean;
  staleSkipDoNotUse: boolean;
  /** Skips sites that move the page's cargo by pipeline. */
  staleSkipPipeline: boolean;
  staleSkipScadaDisabled: boolean;
};

export const DEFAULT_CONTAINER_STATUS_DISPLAY: ContainerStatusDisplayOptions = {
  markStalePolls: true,
  stalePollHours: 5,
  staleIncludeNeverPolled: false,
  staleSkipDoNotDispatch: true,
  staleSkipDoNotUse: true,
  staleSkipPipeline: true,
  staleSkipScadaDisabled: true,
};

export const STALE_POLL_HOURS_MIN = 1;
export const STALE_POLL_HOURS_MAX = 168;

const HOUR = 3_600_000;

/** Saved options over the defaults, dropping anything malformed, so a newer default reaches older saves. */
export const normalizeContainerStatusDisplay = (saved: unknown): ContainerStatusDisplayOptions => {
  const display = {...DEFAULT_CONTAINER_STATUS_DISPLAY};
  if (!saved || typeof saved !== "object") return display;
  const values = saved as Record<string, unknown>;
  for (const key of Object.keys(display) as (keyof ContainerStatusDisplayOptions)[]) {
    const value = values[key];
    if (key === "stalePollHours") {
      if (typeof value === "number" && value >= STALE_POLL_HOURS_MIN && value <= STALE_POLL_HOURS_MAX) display.stalePollHours = value;
    } else if (typeof value === "boolean") {
      display[key] = value;
    }
  }
  return display;
};

export type StalePollContext = {
  now: number;
  /** The page's cargo; the pipeline skip checks that cargo's pipeline, or either when missing. */
  category?: TransportCategory;
};

/**
 * Whether a container has gone `stalePollHours` without a poll. Containers whose readings were left out of the
 * response are never stale: there is nothing to judge.
 */
export const isPollStale = (
  status: ContainerStatusEntity | undefined,
  display: ContainerStatusDisplayOptions,
  {now, category}: StalePollContext,
): boolean => {
  if (!status || !display.markStalePolls) return false;
  if (status.metadata?.measurements?.included === false) return false;
  if (display.staleSkipDoNotDispatch && status.containerDoNotDispatch) return false;
  if (display.staleSkipDoNotUse && status.containerDoNotUse) return false;
  if (display.staleSkipPipeline && status.isPiped(category)) return false;
  if (display.staleSkipScadaDisabled && status.containerScadaMode?.code === scadaMode.disabled.code) return false;

  const polled = status.meterFill?.dttm;
  if (!polled) return display.staleIncludeNeverPolled;
  return now - polled >= display.stalePollHours * HOUR;
};

export const CONTAINER_STATUS_DISPLAY_STORAGE_KEY = "ots.container-status.display.v1";

export const readContainerStatusDisplay = (key = CONTAINER_STATUS_DISPLAY_STORAGE_KEY): ContainerStatusDisplayOptions => {
  try {
    return normalizeContainerStatusDisplay(JSON.parse(localStorage.getItem(key) ?? "null"));
  } catch {
    return DEFAULT_CONTAINER_STATUS_DISPLAY;
  }
};

export const saveContainerStatusDisplay = (display: ContainerStatusDisplayOptions, key = CONTAINER_STATUS_DISPLAY_STORAGE_KEY) => {
  try {
    localStorage.setItem(key, JSON.stringify(display));
  } catch {
    // Private browsing or a full quota: the choice simply lasts for this visit.
  }
};
